import React from "react";
import { Zap, X, AlertTriangle } from "lucide-react";

/**
 * Confirmation modal for any Tradier trade action.
 *
 * Props:
 *   open        boolean
 *   mode        "paper" | "live"          — colors + wording
 *   title       string                    — e.g. "Roll Position"
 *   summary     jsx/text                  — a compact "you're about to…" summary
 *   details     [{label, value}]          — table of order params
 *   busy        boolean                   — disables buttons + shows spinner text
 *   onConfirm   fn()
 *   onCancel    fn()
 */
export const TradeConfirmDialog = ({ open, mode, title, summary, details = [], busy, onConfirm, onCancel }) => {
  if (!open) return null;
  const isLive = mode === "live";
  const accent = isLive ? "#FF3B30" : "#00E676";
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => !busy && onCancel?.()}
      data-testid="trade-confirm-dialog"
    >
      <div className="panel max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="panel-h flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap size={13} style={{ color: accent }} />
            <div className="label-cap" style={{ color: accent }}>
              {isLive ? "SUBMIT TO TRADIER" : "SIMULATE"} · {title}
            </div>
          </div>
          <button onClick={onCancel} disabled={busy} className="text-[#71717A] hover:text-white">
            <X size={14} />
          </button>
        </div>
        <div className="p-4 space-y-3 text-xs">
          {summary && <p className="text-[#A1A1AA] leading-relaxed">{summary}</p>}
          {details.length > 0 && (
            <div className="border border-[#27272A] divide-y divide-[#27272A]">
              {details.map((d) => (
                <div key={d.label} className="flex justify-between px-3 py-1.5 font-mono">
                  <span className="text-[#71717A]">{d.label}</span>
                  <span className="text-white">{d.value}</span>
                </div>
              ))}
            </div>
          )}
          {isLive ? (
            <div className="flex items-start gap-2 border border-[#FF3B30] bg-[rgba(255,59,48,0.06)] p-2">
              <AlertTriangle size={13} className="text-[#FF3B30] flex-shrink-0 mt-0.5" />
              <p className="text-[10px] text-[#FFD9D6] leading-relaxed">
                This will place a real order on your Tradier account. Fills at market or your limit price.
              </p>
            </div>
          ) : (
            <div className="flex items-start gap-2 border border-[#00E676] bg-[rgba(0,230,118,0.06)] p-2">
              <AlertTriangle size={13} className="text-[#00E676] flex-shrink-0 mt-0.5" />
              <p className="text-[10px] text-[#B0F5C9] leading-relaxed">
                Simulate mode: no order will be sent to Tradier. To route a real order, cancel this and flip the header toggle to SUBMIT.
              </p>
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <button
              onClick={onCancel}
              disabled={busy}
              data-testid="trade-confirm-cancel"
              className="btn-outline flex-1 py-2 disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={busy}
              data-testid="trade-confirm-submit"
              className="flex-1 py-2 text-xs font-bold uppercase tracking-widest text-black disabled:opacity-40"
              style={{ background: accent }}
            >
              {busy ? "Submitting…" : isLive ? "Submit to Tradier" : "Log Simulation"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
